/**
 * 統一されたPDFダウンロード処理
 *
 * handle_pdf_download.ts と handle_rirekisho_pdf_download.ts の重複コードを統一
 */

import store from '@src/store';
import { addToast } from '@src/store/slices/toastSlice';
import { generateResumePdfFileName, generateRirekishoPdfFileName } from '@src/utils/pdfFileName';
import {
  downloadPdfBlob,
  handleFetchError,
  PDF_SECURITY_CONFIG,
  sanitizeFileName,
  validatePdfFile,
  validateResponseContentType,
} from './pdfDownloadUtils';

interface PdfDownloadOptions {
  /**
   * ダウンロード対象のURL
   */
  url: string;

  /**
   * ファイル名（デフォルト: 'document.pdf'）
   */
  fileName?: string;

  /**
   * タイムアウト時間（ミリ秒）
   */
  timeout?: number;

  /**
   * 成功時のメッセージ
   */
  successMessage?: string;

  /**
   * エラー時のメッセージプレフィックス
   */
  errorMessagePrefix?: string;

  /**
   * 成功時のコールバック
   */
  onSuccess?: (fileName: string) => void;

  /**
   * エラー時のコールバック
   */
  onError?: (error: string) => void;

  /**
   * ダウンロード開始前のコールバック
   */
  onBeforeDownload?: () => void;

  /**
   * ダウンロード完了後のコールバック（成功・失敗問わず）
   */
  onAfterDownload?: () => void;
}

/**
 * 統一されたPDFダウンロード関数
 *
 * @param options ダウンロードオプション
 * @returns Promise<void>
 */
async function downloadPdfFile(options: PdfDownloadOptions): Promise<void> {
  const {
    url,
    fileName = 'document.pdf',
    timeout = PDF_SECURITY_CONFIG.TIMEOUT_MS,
    successMessage = 'PDFのダウンロードが完了しました',
    errorMessagePrefix = 'PDFダウンロードに失敗しました',
    onSuccess,
    onError,
    onBeforeDownload,
    onAfterDownload,
  } = options;

  // URL検証
  try {
    new URL(url);
  } catch {
    const errorMsg = '無効なURLです';
    store.dispatch(
      addToast({
        message: `${errorMessagePrefix}: ${errorMsg}`,
        severity: 'error',
      })
    );
    onError?.(errorMsg);
    throw new Error(errorMsg);
  }

  // ダウンロード開始前コールバック
  onBeforeDownload?.();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf, */*;q=0.1',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Content-Type検証
    const contentType = response.headers.get('content-type');
    validateResponseContentType(contentType);

    // レスポンスボディを取得
    const blob = await response.blob();

    // PDFファイル検証
    validatePdfFile(blob, blob.type || null);

    // ファイル名のサニタイズ
    const sanitizedFileName = sanitizeFileName(fileName);

    // ファイルダウンロード実行
    downloadPdfBlob(blob, sanitizedFileName);

    // 成功メッセージ表示
    store.dispatch(
      addToast({
        message: successMessage,
        severity: 'success',
      })
    );

    // 成功時コールバック
    onSuccess?.(sanitizedFileName);
  } catch (error) {
    clearTimeout(timeoutId);
    const errorMessage = handleFetchError(error);

    // エラーメッセージ表示
    store.dispatch(
      addToast({
        message: `${errorMessagePrefix}: ${errorMessage}`,
        severity: 'error',
      })
    );

    // エラー時コールバック
    onError?.(errorMessage);

    throw error;
  } finally {
    // 完了後コールバック
    onAfterDownload?.();
  }
}

/**
 * Resume用のPDFダウンロード関数
 *
 * @param url ダウンロードURL
 */
export function downloadResumePdf(url: string): void {
  downloadPdfFile({
    url,
    fileName: generateResumePdfFileName(),
    successMessage: 'PDFのダウンロードが完了しました',
    errorMessagePrefix: 'PDFダウンロードに失敗しました',
  }).catch(() => {
    // エラーは既にdisplayPdfFile内で処理されているため、ここでは何もしない
  });
}

/**
 * Rirekisho用のPDFダウンロード関数
 *
 * @param url ダウンロードURL
 * @param name ファイル名（オプション）
 */
export function downloadRirekishoPdf(url: string, name?: string): void {
  downloadPdfFile({
    url,
    fileName: name || generateRirekishoPdfFileName(),
    successMessage: '履歴書PDFのダウンロードが完了しました',
    errorMessagePrefix: '履歴書PDFダウンロードに失敗しました',
  }).catch(() => {
    // エラーは既にdownloadPdfFile内で処理されているため、ここでは何もしない
  });
}
